import React, { useEffect, useState } from 'react';
import { dataService } from '@rti/shared';

interface NewsArticle {
  title: string;
  description?: string;
  news_introduction?: string;
  conclusion?: string;
  news_market_overview?: string;
  key_market_trends?: string;
  supply_demand_factors?: string;
  publication_date: string;
  source: string;
  website_url?: string;
}

const News: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [exception, setException] = useState(false);
  const [newsList, setNewsList] = useState<NewsArticle[]>([]);
  const [errorMessage, setErrorMessage] = useState('Failed to fetch news data');
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);

  useEffect(() => {
    setLoading(true);
    setException(false);
    dataService.getNewsData()
      .then((data: any) => {
        const articles = data?.articles || [];
        articles.sort((a: NewsArticle, b: NewsArticle) => new Date(b.publication_date).getTime() - new Date(a.publication_date).getTime());
        setNewsList(articles);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setException(true);
      });
  }, []);

  const openModal = (article: NewsArticle) => setSelectedArticle(article);
  const closeModal = () => setSelectedArticle(null);

  return (
    <div id="dashboard">
      <div className="text-3xl flex text-rs-off-white items-center justify-center h-[500px] w-full" style={{ display: loading ? 'flex' : 'none' }}>
        <i className="spinner material-symbols-outlined !text-7xl opacity-30">cached</i>
      </div>
      <div className="h-full flex flex-col p-5 w-full dashboard-shadow max-w-8xl w-full mt-5" style={{ display: loading || exception ? 'none' : 'block' }}>
        <div className="content-toolbar">
          <button className="jet" onClick={() => window.location.href = '/dashboard'}>
            <i className="material-symbols-outlined">arrow_back_ios</i>&ensp;Back to Landing Page
          </button>
        </div>
        <h1 className="pr-4 py-5 text-3xl font-light">Latest News</h1>
        <div className="text-center text-red-500" style={{ display: exception ? 'block' : 'none' }}>{errorMessage}</div>
        <hr className="my-3 pb-5 -mx-5 border-rs-jet-light" />
        <div>
          {newsList.map((article, idx) => (
            <div key={idx} className="bg-rs-off-black shadow-md rounded-lg p-6 mb-5">
              <h2 className="text-xl font-semibold text-rs-dark-green cursor-pointer" onClick={() => openModal(article)}>{article.title}</h2>
              <p className="mt-2">{article.description}</p>
              <div className="flex justify-between items-center mt-5">
                <span className="text-sm opacity-50">{new Date(article.publication_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}: <a className="underline whitespace-nowrap" href={article.website_url} target="_blank" rel="noopener noreferrer" title="View original article">{article.source}</a></span>
                <button className="ml-3 green whitespace-nowrap" onClick={() => openModal(article)}>Read More</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-[rgba(0,0,0,0.7)] flex items-center justify-center z-50">
          <div className="bg-rs-off-black rounded-lg shadow-lg max-w-2xl w-full p-5 sm:p-10">
            <h2 className="text-xl font-normal text-rs-dark-green">{selectedArticle.title}</h2>
            <p className="mb-5 text-lg">{selectedArticle.news_introduction?.length ? selectedArticle.news_introduction : selectedArticle.conclusion}</p>
            {selectedArticle.news_market_overview && !selectedArticle.news_market_overview.startsWith('No specific') && (
              <p className="font-light mt-3"><span className="text-rs-dark-green font-bold">Market Overview:</span> {selectedArticle.news_market_overview}</p>
            )}
            {selectedArticle.key_market_trends && !selectedArticle.key_market_trends.startsWith('No specific') && (
              <p className="font-light mt-3"><span className="text-rs-dark-green font-bold">Key Trends:</span> {selectedArticle.key_market_trends}</p>
            )}
            {selectedArticle.supply_demand_factors && !selectedArticle.supply_demand_factors.startsWith('No specific') && (
              <p className="font-light mt-3"><span className="text-rs-dark-green font-bold">Supply/Demand Factors:</span> {selectedArticle.supply_demand_factors}</p>
            )}
            {(selectedArticle.news_introduction?.length && selectedArticle.conclusion && !selectedArticle.conclusion.startsWith('No specific')) && (
              <p className="font-light mt-3 mb-10"><span className="text-rs-dark-green font-bold">Conclusion:</span> {selectedArticle.conclusion}</p>
            )}
            <div className="flex justify-between items-center mt-5">
              <span className="text-sm opacity-50">{new Date(selectedArticle.publication_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} <a className="underline whitespace-nowrap" href={selectedArticle.website_url} target="_blank" rel="noopener noreferrer" title="View original article">{selectedArticle.source}</a></span>
              <button className="ml-3 green" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
